import axios from "axios";
import { Loader2, Star } from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import { Textarea } from "src/components/ui/textarea";
import { styled } from "styled-components";
import { useLanguage } from "../../context/LanguageContext";
import { BaseUrlApi, ErrorMessage } from "../../lib/api";
import { useAPI } from "../../context/APIContext";
import { useAuth } from "../../context/AuthContext";

export function RatingDialog({
  open,
  onOpenChange,
  apiUrl,
  title = "Rate your experience",
  description = "How would you rate your experience with us?",
  type,
  id,
}) {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { studentCoursesItems } = useAPI();
  const { getAllStudentCourses } = studentCoursesItems;

  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState("");
  const [rateID, setRateID] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [hover, setHover] = useState(0);

  const getRate = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BaseUrlApi}${apiUrl}`);
      if (data.data.length !== 0) {
        let rateItem = {};
        if (type === "course") {
          rateItem = data.data.find(
            (item) => item.course.id === id && item.reviewer.id === user.id
          );
        } else if (type === "user") {
          rateItem = data.data.find(
            (item) => item.user.id === id && item.reviewer.id === user.id
          );
        }
        setMessage(rateItem.message || "");
        setRating(rateItem.rate || 0);
        setRateID(rateItem.bid || null);
      }
    } catch (error) {
      const result = ErrorMessage(error);
      // if (typeof result !== "object")
      //   toast("Error", {
      //     description: result,
      //   });
      setErrors(result);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getRate();
  }, []);

  const handleSubmit = async () => {
    setLoading(true);
    setErrors({});
    try {
      await axios.post(`${BaseUrlApi}${apiUrl}`, {
        message,
        rate: +rating,
      });
      setRating(0);
      onOpenChange(false);
      toast("Success", {
        description: "Rating added successfully ✨",
      });

      if (type === "course") getAllStudentCourses();
      getRate();
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("Error", {
          description: result,
        });
      setErrors(result);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    setLoading(true);
    setErrors({});
    try {
      await axios.put(`${BaseUrlApi}/reviews/${rateID}`, {
        message,
        rate: +rating,
      });
      setRating(0);
      onOpenChange(false);
      toast("Success", {
        description: "Rating updated successfully ✨",
      });

      if (type === "course") getAllStudentCourses();
      getRate();
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("Error", {
          description: result,
        });
      setErrors(result);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[425px]"
        dir={language === "en" ? "ltr" : "rtl"}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <StyledWrapper className="flex flex-col items-center gap-6 justify-center py-4">
          <div className="rating">
            {[5, 4, 3, 2, 1].map((star) => (
              <Fragment key={star}>
                <input
                  type="radio"
                  id={`star-${star}`}
                  name="star-radio"
                  value={star || rating}
                  checked={rating === star}
                  onChange={() => setRating(star)}
                />
                <label
                  htmlFor={`star-${star}`}
                  onMouseEnter={() => setHover(star)}
                  onMouseLeave={() => setHover(0)}
                >
                  <Star
                    className={
                      rating >= star || hover >= star
                        ? "active !bg-transparent"
                        : ""
                    }
                  />
                </label>
              </Fragment>
            ))}
          </div>
          <div className="space-y-2 flex flex-col w-full">
            <label htmlFor="message">{"Message"}</label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={"Enter your message"}
              className="w-full h-24"
            />
            {errors?.message && (
              <p className="text-red-500 font-semibold text-sm">
                {errors.message[0]}
              </p>
            )}
          </div>
        </StyledWrapper>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={rateID ? handleUpdate : handleSubmit}
            disabled={rating === 0 || loading}
          >
            {loading && <Loader2 className="animate-spin" />}
            {rateID ? "Update" : "Submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const StyledWrapper = styled.div`
  .rating {
    display: flex;
    flex-direction: row-reverse;
    gap: 0.3rem;
    --stroke: #666;
    --fill: #ffc73a;
  }

  .rating input {
    appearance: unset;
  }

  .rating label {
    cursor: pointer;
  }

  .rating svg {
    width: 3rem;
    height: 3rem;
    overflow: visible;
    fill: transparent;
    stroke: var(--stroke);
    stroke-linejoin: bevel;
  }

  .rating svg.active {
    animation: idle 4s linear infinite, yippee 0.75s backwards;
    fill: var(--fill);
    stroke: var(--fill);
    stroke-opacity: 0;
    stroke-dasharray: 0;
    stroke-linejoin: miter;
    stroke-width: 8px;
  }

  @keyframes idle {
    from {
      stroke-dashoffset: 24;
    }
  }

  .rating label:hover svg {
    stroke: var(--fill);
  }

  @keyframes yippee {
    0% {
      transform: scale(1);
      fill: var(--fill);
      fill-opacity: 0;
      stroke-opacity: 1;
      stroke: var(--stroke);
      stroke-dasharray: 10;
      stroke-width: 1px;
      stroke-linejoin: bevel;
    }

    30% {
      transform: scale(0);
      fill: var(--fill);
      fill-opacity: 0;
      stroke-opacity: 1;
      stroke: var(--stroke);
      stroke-dasharray: 10;
      stroke-width: 1px;
      stroke-linejoin: bevel;
    }

    30.1% {
      stroke: var(--fill);
      stroke-dasharray: 0;
      stroke-linejoin: miter;
      stroke-width: 8px;
    }

    60% {
      transform: scale(1.2);
      fill: var(--fill);
    }
  }
`;
